import { type ModalTab, parseModalTab } from '@lib/media/modalTab';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { userStore } from '@stores/User';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { debounce } from 'throttle-debounce';

const PREFERENCES_KEY = 'ui_preferences';

const DEFAULT_MOMENT_MODAL_TAB: ModalTab = 'visual';

/** Tables whose column layout this store persists. */
export type GridPreferencesTableId = 'libraryMedia' | 'libraries';

export interface TableColumnPreferences {
  /** Diff from the (possibly data-driven) defaults. `{}` means "use defaults". */
  visibility: Record<string, boolean>;
  /** The full ordered field list. `[]` means "use the table's default order". */
  order: string[];
}

/**
 * The settings-document keys behind each table's column preferences.
 *
 * These strings are FROZEN. `libraryTableColumn*` is already present in real users' saved
 * documents, and `/user/me/settings` takes a whole document with no migration step — renaming a
 * key would silently reset everyone's column layout. Adding a table means adding a row here;
 * nothing existing moves.
 *
 * The wire format stays flat (one key per facet per table) rather than a single nested object for
 * the same reason: the two `libraryTable*` keys already exist in that shape.
 */
const TABLE_PREFERENCE_KEYS: Record<GridPreferencesTableId, { visibility: string; order: string }> =
  {
    libraryMedia: {
      visibility: 'libraryTableColumnVisibility',
      order: 'libraryTableColumnOrder',
    },
    libraries: {
      visibility: 'librariesTableColumnVisibility',
      order: 'librariesTableColumnOrder',
    },
  };

const TABLE_PREFERENCE_IDS = Object.keys(TABLE_PREFERENCE_KEYS) as GridPreferencesTableId[];

const emptyTablePreferences = (): TableColumnPreferences => ({ visibility: {}, order: [] });

export class UIPreferencesStore extends ReactionManager {
  @observable
  soundEnabled: boolean = false;

  @observable
  autoplayVideos: boolean = false;

  @observable
  autoplaymoments: boolean = true;

  @observable
  libraryViewMode: 'list' | 'grid' = 'list';

  // View mode for the *Libraries* page (the list of libraries), independent of
  // `libraryViewMode` above (the media inside one library). Defaults to 'grid':
  // cover art earns its place at a handful of libraries, and that's the current
  // behaviour — note this is the opposite default to `libraryViewMode`.
  @observable
  librariesViewMode: 'list' | 'grid' = 'grid';

  @observable
  removeWatermark: boolean = false;

  @observable
  isLoading = true;

  @observable
  showSearchBarWhileSelectingMoments: boolean = false;

  @observable
  momentModalDefaultTab: ModalTab = DEFAULT_MOMENT_MODAL_TAB;

  // TODO: Decide whether this should stay backend-synced or move to localStorage
  @observable
  mobileTimelineVisible: boolean = false;

  /**
   * Whether this user has ever marked a range on a media's scrub bar.
   *
   * It exists only to retire the one-line hint that teaches the gesture — there is nothing in
   * a progress bar that says it can be dragged, and on touch there is no hover to discover it
   * with. Once it has been done once it never needs saying again, which is why this latches
   * true and is never set back: a hint the user has to dismiss on every media is worse than
   * the hint itself.
   */
  @observable
  hasMarkedMediaRange: boolean = false;

  // Per-table column layout (which columns are shown, and in what order), keyed by table.
  // Read through `useGridColumnPreferences`, which owns the defaults and the merge rules; see
  // `src/lib/utils/gridColumnPreferences.ts`. Only deviations from a table's defaults are stored.
  @observable
  tableColumnPrefs: Record<GridPreferencesTableId, TableColumnPreferences> = {
    libraryMedia: emptyTablePreferences(),
    libraries: emptyTablePreferences(),
  };

  // Callback to focus the search bar input
  searchBarFocusCallback: (() => void) | null = null;

  @action
  toggleShowSearchBarWhileSelectingMoments() {
    this.showSearchBarWhileSelectingMoments = !this.showSearchBarWhileSelectingMoments;

    // Focus the search bar if it's now visible
    if (this.showSearchBarWhileSelectingMoments && this.searchBarFocusCallback) {
      this.searchBarFocusCallback();
    }
  }

  @action
  hideSearchBarWhileSelectingMoments() {
    this.showSearchBarWhileSelectingMoments = false;
  }

  @action
  setSearchBarFocusCallback(callback: (() => void) | null) {
    this.searchBarFocusCallback = callback;
  }

  private isInitialized = false;
  private syncInProgress = false;
  private pendingSync = false;

  // Debounced sync function to prevent excessive API calls
  private debouncedSync = debounce(300, () => this.syncToBackend());

  constructor() {
    super();
    makeObservable(this);

    // Initialize preferences from backend when user is logged in
    this.initializePreferencesWhenReady();

    // Save preferences to backend when they change (debounced)
    // Reading `ownedSettings()` registers every key it covers as a dependency, so a
    // new preference can't be added to the sync payload without also being watched
    // here. It returns a fresh object each call, same as the inline literal it
    // replaced, so the comparison semantics are unchanged.
    this.reaction(
      () => this.ownedSettings(),
      () => {
        this.debouncedSync();
      },
    );

    // @ts-ignore
    window.uiPreferencesStore = this;
  }

  override deinit() {
    // Cancel any pending debounced sync
    this.debouncedSync.cancel();
    super.deinit();
  }

  /**
   * Force immediate sync to backend (bypasses debouncing)
   * Useful for critical operations or when the component is unmounting
   */
  async forceSync(): Promise<void> {
    this.debouncedSync.cancel();
    await this.syncToBackend();
  }

  private async initializePreferencesWhenReady() {
    // Wait for user to be logged in and user data to be loaded before initializing.
    // Use userStore.data when available instead of calling userStore.fetch() to avoid
    // a duplicate /me request on app load (DataFrame autoFetch already triggers one).
    this.reaction(
      () => ({ isLoggedIn: userStore.isLoggedIn, user: userStore.data }),
      ({ isLoggedIn, user }) => {
        if (isLoggedIn && user && !this.isInitialized) {
          this.initializeFromUserData(user);
        }
      },
      { fireImmediately: true },
    );
  }

  private initializeFromUserData(user: { settings?: Record<string, unknown> }) {
    try {
      if (user?.settings) {
        const settings = user.settings;
        runInAction(() => {
          this.soundEnabled = (settings.enableSounds as boolean) ?? false;
          this.autoplayVideos = (settings.autoplayVideos as boolean) ?? false;
          this.autoplaymoments = (settings.autoplaymoments as boolean) ?? true;
          this.libraryViewMode = (settings.libraryViewMode as 'list' | 'grid') ?? 'list';
          // Validated rather than blind-cast (cf. momentModalDefaultTab below):
          // an unrecognized stored value degrades to the default instead of
          // reaching the view switch and rendering nothing.
          const storedLibrariesViewMode = settings.librariesViewMode;
          this.librariesViewMode =
            storedLibrariesViewMode === 'list' || storedLibrariesViewMode === 'grid'
              ? storedLibrariesViewMode
              : 'grid';
          this.removeWatermark = (settings.removeWatermark as boolean) ?? false;
          // A tab stored by a newer or older build degrades to the default rather than
          // reaching the panel switch as a value it cannot render. `user.settings` has no
          // backend schema, so this is the only thing standing between a stored string and a
          // panel lookup.
          this.momentModalDefaultTab =
            parseModalTab(settings.momentModalDefaultTab) ?? DEFAULT_MOMENT_MODAL_TAB;
          this.mobileTimelineVisible = (settings.mobileTimelineVisible as boolean) ?? false;
          this.hasMarkedMediaRange = (settings.hasMarkedMediaRange as boolean) ?? false;
          // One loop over the registry, so a newly registered table is hydrated without a
          // matching edit here. A stored value of the wrong shape degrades to "use defaults".
          for (const tableId of TABLE_PREFERENCE_IDS) {
            const keys = TABLE_PREFERENCE_KEYS[tableId];
            const storedVisibility = settings[keys.visibility];
            const storedOrder = settings[keys.order];
            this.tableColumnPrefs[tableId] = {
              visibility: isPlainBooleanRecord(storedVisibility) ? storedVisibility : {},
              order: isStringArray(storedOrder) ? storedOrder : [],
            };
          }
          this.isInitialized = true;
          this.isLoading = false;
        });
      } else {
        runInAction(() => {
          this.isInitialized = true;
          this.isLoading = false;
        });
      }
    } catch (error) {
      console.error('Failed to initialize UI preferences from user data:', error);
      runInAction(() => {
        this.isInitialized = true;
        this.isLoading = false;
      });
    }
  }

  /**
   * The settings keys this store owns, read at call time.
   *
   * Single source of truth for "which keys are ours", used by the sync reaction (so
   * reading them registers the dependencies), by the POST body, and by the
   * write-back. Previously the same list appeared in all three places, and omitting
   * it from the reaction was a silent failure: the preference worked all session and
   * never persisted.
   */
  private ownedSettings() {
    return {
      enableSounds: this.soundEnabled,
      autoplayVideos: this.autoplayVideos,
      autoplaymoments: this.autoplaymoments,
      libraryViewMode: this.libraryViewMode,
      librariesViewMode: this.librariesViewMode,
      removeWatermark: this.removeWatermark,
      momentModalDefaultTab: this.momentModalDefaultTab,
      mobileTimelineVisible: this.mobileTimelineVisible,
      hasMarkedMediaRange: this.hasMarkedMediaRange,
      ...this.tableColumnSettings(),
    };
  }

  /**
   * Flattens the per-table column preferences back onto their wire keys (see
   * `TABLE_PREFERENCE_KEYS`).
   *
   * Spreading this into `ownedSettings()` is what registers every table's preferences as a
   * dependency of the sync reaction, so a newly registered table can't hit the silent
   * "works all session, never persists" failure that comment describes.
   */
  private tableColumnSettings(): Record<string, unknown> {
    const settings: Record<string, unknown> = {};
    for (const tableId of TABLE_PREFERENCE_IDS) {
      const keys = TABLE_PREFERENCE_KEYS[tableId];
      const prefs = this.tableColumnPrefs[tableId];
      settings[keys.visibility] = prefs.visibility;
      settings[keys.order] = prefs.order;
    }
    return settings;
  }

  private async syncToBackend(retryCount = 0): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    // If sync is already in progress, mark that we have pending changes
    if (this.syncInProgress) {
      this.pendingSync = true;
      return;
    }

    this.syncInProgress = true;

    try {
      // Use cached user when available to avoid a second /me on load (DataFrame autoFetch
      // already triggers one). Only call fetch() when we don't have user data yet.
      const user = userStore.data ?? (await userStore.fetch());
      const currentSettings = user?.settings || {};

      // Create updated settings object
      const updatedSettings = { ...currentSettings, ...this.ownedSettings() };

      // Send to backend
      await userStore.apiWithAuth('/user/me/settings', {
        method: 'POST',
        body: updatedSettings,
      });

      // Mirror our keys back into the store. This is not bookkeeping — it is what
      // stops two writers clobbering each other. `/user/me/settings` takes a WHOLE
      // settings document, and this store is not its only writer: UserSettingsService
      // (favorites, and anything else via get/setUserSetting) POSTs its own cache,
      // which it keeps in step with `userStore.data.settings` through a MobX reaction.
      // Without this, that cache kept the pre-change value and the service's next
      // write silently reverted whatever preference had just been saved — on the
      // Libraries page, a two-click flow: switch Gallery → List, pin a library, and
      // the view mode was gone on reload.
      //
      // Three things about the shape here, each load-bearing:
      //
      // 1. Merge onto a FRESH read of `settings`, never the pre-await snapshot or the
      //    server's echo of it. A concurrent favorites write that landed during our
      //    request is present in the observable but absent from that snapshot, so
      //    assigning it wholesale would erase the pin locally as well as on the
      //    server — turning a loss the next favorites write would have repaired into
      //    a permanent one.
      // 2. Re-read our own values too (`ownedSettings()` rather than the snapshot), so
      //    a retry landing after a newer sync stamps current truth instead of
      //    resurrecting the values it happened to capture.
      // 3. Assign the nested property IN PLACE rather than replacing `userStore.data`.
      //    `DataFrame.data` is deeply observable, so this still notifies the service's
      //    reaction — but replacing the object changes the identity that
      //    `TrackingStore` watches (`reaction(() => userStore.data, …)`), which would
      //    re-fire `mixpanel.identify` + `people.set` on every preference sync,
      //    including each moment-modal tab switch and column toggle.
      runInAction(() => {
        if (!userStore.data) return;
        userStore.data.settings = { ...userStore.data.settings, ...this.ownedSettings() };
      });

      // Reset retry count on success
      retryCount = 0;
    } catch (error) {
      console.error('Failed to sync UI preferences to backend:', error);

      // Retry logic with exponential backoff
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Max 5 seconds
        console.log(`Retrying sync in ${delay}ms (attempt ${retryCount + 1}/3)`);

        setTimeout(() => {
          this.syncToBackend(retryCount + 1);
        }, delay);
        return; // Don't reset syncInProgress yet
      } else {
        console.error('Max retry attempts reached for UI preferences sync');
        // Could emit an event here for user notification if needed
      }
    } finally {
      this.syncInProgress = false;

      // If there were pending changes while we were syncing, trigger another sync
      if (this.pendingSync) {
        this.pendingSync = false;
        // Use a small delay to prevent immediate retry
        setTimeout(() => this.syncToBackend(), 100);
      }
    }
  }

  @action
  toggleSounds() {
    this.soundEnabled = !this.soundEnabled;
  }

  @action
  setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
  }

  @action
  toggleAutoplayVideos() {
    this.autoplayVideos = !this.autoplayVideos;
  }

  @action
  setAutoplayVideos(enabled: boolean) {
    this.autoplayVideos = enabled;
  }

  @action
  toggleAutoplayMoments() {
    this.autoplaymoments = !this.autoplaymoments;
  }

  @action
  setAutoplayMoments(enabled: boolean) {
    this.autoplaymoments = enabled;
  }

  @action
  setLibraryViewMode(mode: 'list' | 'grid') {
    this.libraryViewMode = mode;
  }

  @action
  toggleLibraryViewMode() {
    this.libraryViewMode = this.libraryViewMode === 'list' ? 'grid' : 'list';
  }

  @action
  setLibrariesViewMode(mode: 'list' | 'grid') {
    this.librariesViewMode = mode;
  }

  @action
  setRemoveWatermark(value: boolean) {
    this.removeWatermark = value;
  }

  @action
  setMomentModalDefaultTab(tab: ModalTab) {
    this.momentModalDefaultTab = tab;
  }

  @action
  toggleMobileTimelineVisible() {
    this.mobileTimelineVisible = !this.mobileTimelineVisible;
  }

  /** Latches — see the field. Never unset. */
  @action
  markMediaRangeUsed() {
    this.hasMarkedMediaRange = true;
  }

  /**
   * Patches one table's column preferences.
   *
   * Replaces the whole per-table entry rather than mutating a facet, so each write produces
   * exactly one new identity for the consuming memos — and an untouched facet keeps its identity,
   * so a reorder doesn't invalidate the visibility model (or vice versa).
   */
  @action
  setTableColumnPreferences(
    tableId: GridPreferencesTableId,
    patch: Partial<TableColumnPreferences>,
  ) {
    const current = this.tableColumnPrefs[tableId];
    this.tableColumnPrefs[tableId] = {
      visibility: patch.visibility ? { ...patch.visibility } : current.visibility,
      order: patch.order ? [...patch.order] : current.order,
    };
  }
}

function isPlainBooleanRecord(value: unknown): value is Record<string, boolean> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((v) => typeof v === 'boolean');
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

export const { Provider: UIPreferencesProvider, useStore: useUIPreferences } = createContext(
  UIPreferencesStore,
  {
    dispose: (store) => store.deinit(),
  },
);
